"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "../api/axios"
import { toast } from "react-toastify"
import { Eye, EyeOff, Mail, Lock, LogIn } from "lucide-react"

function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const [user, setUser] = useState(null)

  useEffect(() => {
    const checkUser = async () => {
      try {
        const res = await axios.get("/api/user", { withCredentials: true })
        const loggedInUser = res.data
        setUser(loggedInUser)

        if (loggedInUser.role_id === 3) {
          navigate("/user")
        } else if (loggedInUser.role_id === 1 || loggedInUser.role_id === 2) {
          navigate("/dashboard")
        }
      } catch {
        setUser(null)
      }
    }

    checkUser()
  }, [navigate])

  const handleLogin = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await axios.get("/sanctum/csrf-cookie")
      const res = await axios.post("/api/login", { email, password })

      const user = res.data.user || res.data

      if (user.role_id === 1) {
        navigate("/dashboard")
      } else if (user.role_id === 2) {
        navigate("/dashboard")
      } else if (user.role_id === 3) {
        navigate("/user")
      } else {
        toast.error("Unknown role")
      }
      window.location.reload()
    } catch (err) {
      console.error(err.response?.data)
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[Object.keys(err.response.data.errors)[0]][0] ||
        "Login failed. Please try again."

      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          {/* <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl mb-6 shadow-lg">
            <img
              src="https://res.cloudinary.com/dwfyw8vci/image/upload/v1752604816/kspinc-webplogo_opfd66.webp"
              alt="KSP Inc Logo"
              className="w-12 h-12 object-contain"
              onError={(e) => {
                e.target.style.display = "none"
                e.target.nextSibling.style.display = "flex"
              }}
            />
            <span className="text-white font-bold text-2xl" style={{ display: "none" }}>
              K
            </span>
          </div> */}
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Welcome Back</h1>
          <p className="text-gray-600 text-lg">Sign in to your KSPData account</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail size={20} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-lg"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock size={20} className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-14 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-lg"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <button type="button" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                Forgot password?
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 px-4 rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <LogIn size={20} />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          {/* <div className="mt-8 p-4 bg-orange-50 rounded-xl border border-orange-100">
            <p className="text-xs text-orange-700 text-center mb-2 font-semibold">Demo Credentials:</p>
            <div className="text-xs text-orange-600 space-y-1">
              <p>
                <strong>Admin:</strong> admin@example.com / password
              </p>
              <p>
                <strong>User:</strong> user@example.com / password
              </p>
            </div>
          </div> */}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Don't have an account?{" "}
            <button className="text-orange-600 hover:text-orange-700 font-medium">Contact Administrator</button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
